import { JSX, useEffect, useState, useRef } from 'react';
import { useStore } from '@betgames/bg-state-manager';
import { AnimationControls, timeline as motionTimeline } from 'motion';
import {
    NOTIFICATION_LONG_TIMEOUT,
    authStore,
    t,
    gamificationSelectors,
    gamificationStore,
    GameMessageType,
    GameMessagePlacement,
    gamesMessagesEntity,
    explodingStarsAnimation,
    EASING_OPTION,
} from '@bg-shared';
import classes from './LevelUpMessage.module.scss';

const ANIMATION_DURATION = 2;
const STAR_DELAY = 0.4;

export const LevelUpMessage = (): JSX.Element => {
    const isLoaded = useStore(gamificationStore, gamificationSelectors.isLoaded);
    const level = useStore(gamificationStore, gamificationSelectors.getLevel);
    const authorized = useStore(authStore);
    const message = useStore(gamesMessagesEntity.store(GameMessagePlacement.ABSOLUTE));
    const [shouldAnimate, setShouldAnimate] = useState<boolean>(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const starsContainerRef = useRef<HTMLDivElement>(null);

    const animationFrameRef = useRef<number>();
    const timelineRef = useRef<AnimationControls>();
    const animationInstanceRef = useRef<AnimationControls>();

    useEffect(() => {
        if (message?.type !== GameMessageType.LEVEL_UP) {
            return;
        }

        setShouldAnimate(true);
    }, [message]);

    useEffect(() => {
        if (
            !shouldAnimate ||
            !containerRef.current ||
            !canvasRef.current ||
            !starsContainerRef.current
        ) {
            return undefined;
        }

        const startAnimation = () => {
            if (!containerRef.current || !canvasRef.current || !starsContainerRef.current) {
                console.warn('Refs not available for animation');
                return;
            }

            const containerElement = containerRef.current;
            const canvasElement = canvasRef.current;
            const starsContainerElement = starsContainerRef.current;

            const width = containerElement.offsetWidth;
            const height = containerElement.offsetHeight;

            canvasElement.width = width;
            canvasElement.height = height;

            const centerX = width / 2;
            const positionY = height * 0.4;

            explodingStarsAnimation.init(
                { x: centerX, y: positionY },
                { x: centerX, y: positionY * 1.3 },
                canvasElement.getContext('2d'),
                ANIMATION_DURATION,
                1.8,
            );
            animationInstanceRef.current = explodingStarsAnimation.run(STAR_DELAY);

            timelineRef.current = motionTimeline([
                [
                    containerElement,
                    {
                        opacity: [0, 1],
                    },
                ],
                [
                    starsContainerElement,
                    {
                        transform: [
                            'translate(-50%, -50%) scale(0.1)',
                            'translate(-50%, -50%) scale(1)',
                        ],
                        opacity: [null, 1],
                    },
                    { duration: STAR_DELAY, easing: EASING_OPTION.EASE_OUT_BACK },
                ],
                [
                    starsContainerElement,
                    {
                        transform: [null, 'translate(-50%, -120%)'],
                        opacity: [null, 0],
                    },
                    {
                        at: +ANIMATION_DURATION,
                    },
                ],
                [
                    containerElement,
                    {
                        opacity: [1, 0],
                    },
                ],
            ]);

            timelineRef.current.finished.then(() => {
                setShouldAnimate(false);
            });
        };

        // Use requestAnimationFrame to ensure DOM is painted
        animationFrameRef.current = requestAnimationFrame(startAnimation);

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }

            if (timelineRef.current) {
                timelineRef.current.cancel();
            }

            if (animationInstanceRef.current) {
                animationInstanceRef.current.cancel();
            }
        };
    }, [shouldAnimate]);

    useEffect(() => {
        if (!authorized || !isLoaded || !level || level.number === level.previousLevel) {
            return;
        }

        gamesMessagesEntity.addToQueue(
            {
                message: t.gamification('level_up'),
                timeout: NOTIFICATION_LONG_TIMEOUT,
                type: GameMessageType.LEVEL_UP,
            },
            GameMessagePlacement.ABSOLUTE,
        );
    }, [authorized, isLoaded, level?.number]);

    if (!authorized || !isLoaded || !shouldAnimate) {
        return null;
    }

    return (
        <>
            <div className={classes.backdrop} />
            <div ref={containerRef} data-qa="area-level-up-message" className={classes.container}>
                <canvas ref={canvasRef} />
                <div ref={starsContainerRef} className={classes.radialGradient}>
                    <div className={classes.star}>
                        <div className={classes.levelNumber}>{level.number}</div>
                        <div className={classes.label}>{message?.message}</div>
                    </div>
                </div>
            </div>
        </>
    );
};
